// CSV generation.
//
// This is load-bearing for the product's whole pitch — "leave in an afternoon
// with all your data" — so it is a tested pure module rather than a join()
// inside a click handler. Three things here are not optional:
//
//  1. Quoting. A member called O'Brien is fine; one whose notes contain a
//     comma, a quote or a newline will silently shear a row in half otherwise,
//     and the operator will not notice until the import fails somewhere else.
//  2. A UTF-8 BOM. Excel on Windows reads a BOM-less UTF-8 file as the system
//     codepage, so "Raghunathan" survives and "Adeyemí" does not.
//  3. Formula neutralisation. A value starting = + - @ is executed by Excel
//     and Sheets when the file is opened. That is a real attack: a member types
//     a formula into their own name field, the gym exports, and the formula
//     runs on the owner's machine. Prefixing a tab keeps the text visible and
//     inert.

export const BOM = '﻿';

const NEEDS_QUOTES = /[",\n\r]/;
const FORMULA_START = /^[=+\-@\t\r]/;
// A plain number cannot be a formula, and escaping one would turn a money
// column into text — a refund of -179.00 would stop summing in the spreadsheet
// it was exported for. Only genuine expressions get neutralised.
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

/** One CSV field: neutralised, quoted, escaped. */
export function csvValue(value) {
  if (value === null || value === undefined) return '';

  let text;
  if (value instanceof Date) text = value.toISOString();
  else if (typeof value === 'boolean') text = value ? 'yes' : 'no';
  else if (typeof value === 'object') text = JSON.stringify(value);
  else text = String(value);

  if (text === '') return '';
  if (FORMULA_START.test(text) && !PLAIN_NUMBER.test(text)) text = `'${text}`;
  if (NEEDS_QUOTES.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

/**
 * Rows to CSV text.
 *
 * columns: [{ key, header, value? }] — `value(row)` overrides plain key lookup,
 * so a derived column (a plan's name against a membership's plan id) does not
 * need the caller to pre-flatten every row.
 *
 * CRLF line endings, because that is what RFC 4180 says and what every
 * spreadsheet on Windows expects.
 */
export function toCsv(rows, columns) {
  const header = columns.map((c) => csvValue(c.header ?? c.key)).join(',');
  const body = rows.map((row) => columns
    .map((c) => csvValue(c.value ? c.value(row) : row[c.key]))
    .join(','));
  return [header, ...body].join('\r\n');
}

/** A complete file, ready to hand to a browser or write to disk. */
export function csvFile(rows, columns) {
  return BOM + toCsv(rows, columns);
}
